/*
 * RUNNING A WIDGET THE WAY A BOARD WOULD, TO SEE WHETHER IT HOLDS.
 *
 * The author of a widget — a person or the assistant — should not have to place a tile on a board to
 * find out that its text falls outside the tile at a narrow size, or that a colour it chose is one the
 * renderer drops. This mounts the widget in the REAL host, off screen, at three tile sizes, once with
 * a reading and once without, and reports what the runtime saw: whether it started, whether it
 * painted, and every way its scenes fell below the floor (runtime/scene-check.ts).
 *
 * The real host, not a private copy: a check that ran the widget differently from the board would be
 * a check of something nobody ships. That also means the sandbox's own protections apply — a widget
 * that hangs is caught by the host's load deadline rather than by this.
 */
import type { TypedValue } from '../core/bridge/connect-client';
import { DENY_ALL, type CapabilityGrant } from './runtime/capability';
import './widget-host';

/** A preview may read nothing and never expires: DENY_ALL's zero expiry would fault it at once. */
const GRANT: CapabilityGrant = { ...DENY_ALL, expiresAtMs: null };

/** The sizes a tile is actually placed at: one cell, a common tile, a wide one. */
const SIZES: [number, number][] = [
  [170, 110],
  [340, 200],
  [720, 300],
];

/** How long one pass is given: the host's own load deadline is 8s, and a widget that has not painted
 *  in two seconds at one size has nothing more to show that waiting would reveal. */
const PASS_MS = 2200;

export interface WidgetCheckResult {
  /** It started, painted, and nothing fell below the floor. */
  ok: boolean;
  started: boolean;
  painted: boolean;
  /** What the host reported when it gave up on the widget, or ''. */
  fault: string;
  /** Every distinct problem, across every size and both passes. */
  problems: string[];
}

/** One pass: mount, wait, tear down. Resolves what the host and frame reported. */
function pass(source: string, config: Record<string, unknown>, values: TypedValue[] | undefined, width: number, height: number): Promise<{ started: boolean; painted: boolean; fault: string; problems: string[] }> {
  return new Promise((resolve) => {
    const holder = document.createElement('div');
    // Off screen rather than hidden: `display: none` or `visibility: hidden` would give the frame a
    // zero box and stop the animation frames the widget paints on, so it would report nothing at all.
    holder.style.cssText = `position: fixed; left: -10000px; top: 0; width: ${width}px; height: ${height}px; pointer-events: none;`;
    const host = document.createElement('vitesse-widget-host');
    Object.assign(host, { source, config, grant: GRANT, previewValues: values ?? [] });
    host.style.cssText = 'display: block; width: 100%; height: 100%;';

    const out = { started: false, painted: false, fault: '', problems: [] as string[] };
    host.addEventListener('widget-ready', () => { out.started = true; });
    host.addEventListener('widget-problems', (e: Event) => { out.problems = (e as CustomEvent<string[]>).detail; });
    host.addEventListener('widget-fault', (e: Event) => { out.fault = String((e as CustomEvent<string>).detail); });

    holder.appendChild(host);
    document.body.appendChild(holder);
    window.setTimeout(() => {
      // THAT IT DREW, read the way anything outside the frame has to read it. The frame's document is
      // an opaque origin, so the only evidence a scene reached the screen is the text the host
      // publishes from it for screen readers. A widget that drew a picture with no text in it at all
      // is rare enough — and unreadable enough — that "nothing was drawn" is the right reading.
      out.painted = (host.getAttribute('data-screen-text') ?? '') !== '';
      holder.remove();
      resolve(out);
    }, PASS_MS);
  });
}

/**
 * Check one widget's source. `config` is what a tile would pass it; a reading is synthesised for the
 * symbol it names, so the widget is seen both with a value and with none — the null pass is where a
 * widget usually prints NaN.
 */
export async function checkWidgetSource(source: string, config: Record<string, unknown> = {}): Promise<WidgetCheckResult> {
  const symbolId = typeof config.symbolId === 'string' ? config.symbolId : '';
  const live: TypedValue[] = symbolId ? [{ symbolId, value: 42.5, num: 42.5, tsMs: Date.now() }] : [];

  const result: WidgetCheckResult = { ok: false, started: false, painted: false, fault: '', problems: [] };
  const seen = new Set<string>();
  for (const [w, h] of SIZES) {
    for (const values of [live, []]) {
      const r = await pass(source, config, values, w, h);
      result.started ||= r.started;
      result.painted ||= r.painted;
      result.fault ||= r.fault;
      for (const p of r.problems) if (!seen.has(p)) { seen.add(p); result.problems.push(`${w}×${h}${values.length ? '' : ' with no reading'}: ${p}`); }
      if (r.fault) break; // a widget that faulted says the same thing at every size
    }
    if (result.fault) break;
  }
  result.ok = result.started && result.painted && !result.fault && result.problems.length === 0;
  return result;
}
